// theme/presets/color.ts — small colour-math helpers for derived accent tokens.
//
// Used by applyPreferences to derive the dependent accent tokens (border tint,
// glow, focus ring, on-accent text) from a single accent colour — for both the
// AA-verified presets and an arbitrary user-picked custom colour (the wheel).

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

/** Parse '#rgb' or '#rrggbb' → {r,g,b}. Invalid input falls back to mid-grey. */
export function hexToRgb(hex: string): Rgb {
  const h = (hex || '').replace('#', '').trim();
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return { r: 128, g: 128, b: 128 };
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

/** 'rgba(r, g, b, a)' string from a hex + alpha. */
export function rgba(hex: string, alpha: number): string {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function channelLum(c: number): number {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

/** WCAG relative luminance (0–1) from raw 0–255 channels. */
export function luminanceRgb(r: number, g: number, b: number): number {
  return 0.2126 * channelLum(r) + 0.7152 * channelLum(g) + 0.0722 * channelLum(b);
}

/** WCAG relative luminance (0–1). */
export function relativeLuminance(hex: string): number {
  const { r, g, b } = hexToRgb(hex);
  return luminanceRgb(r, g, b);
}

/** WCAG contrast ratio between two known luminances. */
export function contrastRatioL(la: number, lb: number): number {
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/** WCAG contrast ratio between two hex colours. */
export function contrastRatio(a: string, b: string): number {
  return contrastRatioL(relativeLuminance(a), relativeLuminance(b));
}

/** sRGB hex → HSL (h 0–360, s/l 0–1). */
export function hexToHsl(hex: string): { h: number; s: number; l: number } {
  const { r, g, b } = hexToRgb(hex);
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return { h: 0, s: 0, l };
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) * 60;
  else if (max === gn) h = ((bn - rn) / d + 2) * 60;
  else h = ((rn - gn) / d + 4) * 60;
  return { h, s, l };
}

/** HSL (h 0–360, s/l 0–1) → sRGB hex. */
export function hslToHex(h: number, s: number, l: number): string {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hp = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  const m = l - c / 2;
  let rgb: [number, number, number];
  if (hp < 1) rgb = [c, x, 0];
  else if (hp < 2) rgb = [x, c, 0];
  else if (hp < 3) rgb = [0, c, x];
  else if (hp < 4) rgb = [0, x, c];
  else if (hp < 5) rgb = [x, 0, c];
  else rgb = [c, 0, x];
  const to255 = (v: number) => Math.round(Math.min(1, Math.max(0, v + m)) * 255);
  return `#${rgb.map((v) => to255(v).toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Pick the more-legible on-accent text colour for a fill of `hex`, comparing the
 * contrast of near-black vs white against it (not a naive luminance threshold —
 * a mid-bright accent like #B57BFF needs DARK text, ~7:1, not white at ~2.9:1).
 */
export function onAccentFor(hex: string): string {
  const L = relativeLuminance(hex);
  const contrastWhite = 1.05 / (L + 0.05);
  const contrastDark = (L + 0.05) / 0.05; // near-black luminance ≈ 0
  return contrastDark >= contrastWhite ? '#04060D' : '#FFFFFF';
}

/** Linear mix of two hex colours by `amount` (0 → hex, 1 → withHex). */
export function mix(hex: string, withHex: string, amount: number): string {
  const a = hexToRgb(hex);
  const b = hexToRgb(withHex);
  const to = (x: number, y: number) => Math.round(x + (y - x) * amount);
  const r = to(a.r, b.r);
  const g = to(a.g, b.g);
  const bl = to(a.b, b.b);
  return `#${[r, g, bl].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
}

export const darken = (hex: string, amt: number): string => mix(hex, '#000000', amt);
export const lighten = (hex: string, amt: number): string => mix(hex, '#FFFFFF', amt);

/**
 * Composite a translucent `rgba()`/`rgb()` colour over an opaque hex background and
 * return the resulting OPAQUE `rgb()` string. Used to derive the de-glassed
 * (glass-off) surface fill: what the translucent glass token looks like flattened
 * onto the app background — but guaranteed opaque, so the animated backdrop cannot
 * bleed through when the user has glass cards turned OFF.
 */
export function flatten(color: string, overHex: string): string {
  const m = color.match(/rgba?\(([^)]+)\)/i);
  if (!m || !m[1]) return color;
  const parts = m[1].split(',').map((s) => parseFloat(s.trim()));
  const r = parts[0] ?? 0;
  const g = parts[1] ?? 0;
  const b = parts[2] ?? 0;
  const a = parts[3] ?? 1;
  const bg = hexToRgb(overHex);
  const over = (fg: number, bc: number) => Math.round(fg * a + bc * (1 - a));
  return `rgb(${over(r, bg.r)}, ${over(g, bg.g)}, ${over(b, bg.b)})`;
}
